import { Component, OnInit, inject } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { trigger, transition, style, animate } from '@angular/animations';
import { ToastrModule } from 'ngx-toastr';
import { TitleService } from './core/services/title.service';
import { ThemeService } from './core/services/theme.service';

import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ToastModule } from 'primeng/toast';
import { BusinessTermDrawerComponent } from './shared/components/business-term-drawer/business-term-drawer.component';

@Component({
  selector: 'elx-root',
  template: `
    <router-outlet></router-outlet>
    <div toastContainer class="toast-container"></div>
    <p-confirmDialog
      id="global-confirm-dialog"
      header="Confirmation"
      appendTo="body"
      aria-label="Confirmation dialog"
      closeAriaLabel="Close"
      acceptAriaLabel="Confirm"
      rejectAriaLabel="Cancel"
    ></p-confirmDialog>
    <p-toast></p-toast>
    <elx-business-term-drawer></elx-business-term-drawer>
  `,
  standalone: true,
  imports: [
    RouterOutlet,
    ToastrModule,
    ConfirmDialogModule,
    ToastModule,
    BusinessTermDrawerComponent,
  ],
  animations: [
    trigger('fadeAnimation', [
      transition('* => *', [
        style({ opacity: 0 }),
        animate('300ms ease-in-out', style({ opacity: 1 })),
      ]),
    ]),
  ],
})
export class AppComponent implements OnInit {
  private _titleService = inject(TitleService);
  private _themeService = inject(ThemeService);

  ngOnInit(): void {
    // Initialize title service to handle dynamic title updates for all routes
    this._titleService.initialize();

    // Run theme loading after bootstrap so it can never block app rendering.
    void this._themeService.loadFromAPI();

    // Ensure PrimeNG dialogs have accessible names even when aria-labelledby is unreliable.
    // This improves Lighthouse a11y without changing PrimeNG internals.
    this.ensureDialogsHaveAccessibleNames();
  }

  private ensureDialogsHaveAccessibleNames(): void {
    if (typeof window === 'undefined' || typeof document === 'undefined') return;

    const hasAccessibleName = (el: Element): boolean => {
      const ariaLabel = el.getAttribute('aria-label');
      const ariaLabelledBy = el.getAttribute('aria-labelledby');
      return !!(ariaLabel && ariaLabel.trim()) || !!(ariaLabelledBy && ariaLabelledBy.trim());
    };

    const patchDialog = (dialog: Element): void => {
      if (hasAccessibleName(dialog)) return;

      // Try to derive a label from the visible header, otherwise fall back.
      const header =
        dialog.querySelector('.p-dialog-title')?.textContent?.trim() ||
        dialog.querySelector('.p-confirm-dialog-message')?.textContent?.trim() ||
        'Dialog';

      dialog.setAttribute('aria-label', header || 'Dialog');
    };

    const scan = (): void => {
      const dialogs = document.querySelectorAll(
        'p-dialog[role="dialog"], p-dialog[role="alertdialog"]',
      );
      dialogs.forEach((d) => patchDialog(d));
    };

    // Initial scan + keep patching when dialogs appear.
    scan();
    const observer = new MutationObserver(() => scan());
    observer.observe(document.body, { childList: true, subtree: true });
  }
}
